import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  Renderer2,
  HostListener,
} from '@angular/core';

/**
 * Drop-in replacement for PrimeNG's pTooltip directive.
 *
 * Usage is identical to before:
 *   <button pTooltip="Save current answers"></button>
 *   <span class="icon" pTooltip="Edit"></span>
 *
 * To migrate: just change the import from 'primeng/tooltip' to this file,
 * and rename the directive import from `Tooltip`/`TooltipModule` to `AppTooltipDirective`.
 * No template changes needed.
 */
@Directive({
  selector: '[pTooltip]',
  standalone: true,
})
export class AppTooltipDirective implements OnDestroy {
  /** Tooltip text — same input name as PrimeNG so templates don't change */
  @Input('pTooltip') tooltipText: string = '';

  /** Optional: tooltipPosition="top" | "bottom" | "left" | "right" (default: top) */
  @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

  /** Matches PrimeNG's [showDelay] input — ms before the tooltip appears (default: 150) */
  @Input() showDelay: number = 150;

  /** Matches PrimeNG's [hideDelay] input — ms before the tooltip disappears (default: 0) */
  @Input() hideDelay: number = 0;

  private tooltipEl: HTMLElement | null = null;
  private showTimeout: ReturnType<typeof setTimeout> | null = null;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2
  ) {}

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (!this.tooltipText) return;
    this.clearHideTimeout();
    this.showTimeout = setTimeout(() => this.show(), this.showDelay);
  }

  @HostListener('mouseleave')
  @HostListener('click')
  onMouseLeave(): void {
    this.clearShowTimeout();
    if (this.hideDelay > 0) {
      this.hideTimeout = setTimeout(() => this.hide(), this.hideDelay);
    } else {
      this.hide();
    }
  }

  @HostListener('window:scroll')
  onScroll(): void {
    this.hide();
  }

  private show(): void {
    if (this.tooltipEl) return; // already showing

    const tooltip = this.renderer.createElement('div');
    this.renderer.addClass(tooltip, 'app-tooltip');
    this.renderer.addClass(tooltip, `app-tooltip-${this.tooltipPosition}`);

    const text = this.renderer.createText(this.tooltipText);
    this.renderer.appendChild(tooltip, text);
    this.renderer.appendChild(document.body, tooltip);

    this.tooltipEl = tooltip;
    this.positionTooltip();

    // fade in on next frame so the CSS transition runs
    requestAnimationFrame(() => {
      if (this.tooltipEl) {
        this.renderer.addClass(this.tooltipEl, 'app-tooltip-visible');
      }
    });
  }

  private hide(): void {
    if (!this.tooltipEl) return;
    const el = this.tooltipEl;
    this.tooltipEl = null;
    this.renderer.removeClass(el, 'app-tooltip-visible');
    setTimeout(() => {
      if (el.parentNode) {
        this.renderer.removeChild(document.body, el);
      }
    }, 120); // matches CSS transition duration
  }

  private positionTooltip(): void {
    if (!this.tooltipEl) return;
    const hostRect = this.el.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipEl.getBoundingClientRect();
    const gap = 8;

    let top = 0;
    let left = 0;

    switch (this.tooltipPosition) {
      case 'top':
        top = hostRect.top - tooltipRect.height - gap;
        left = hostRect.left + hostRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = hostRect.bottom + gap;
        left = hostRect.left + hostRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'left':
        top = hostRect.top + hostRect.height / 2 - tooltipRect.height / 2;
        left = hostRect.left - tooltipRect.width - gap;
        break;
      case 'right':
        top = hostRect.top + hostRect.height / 2 - tooltipRect.height / 2;
        left = hostRect.right + gap;
        break;
    }

    // Keep tooltip within viewport horizontally
    const maxLeft = window.innerWidth - tooltipRect.width - 4;
    left = Math.max(4, Math.min(left, maxLeft));

    this.renderer.setStyle(this.tooltipEl, 'top', `${top + window.scrollY}px`);
    this.renderer.setStyle(this.tooltipEl, 'left', `${left + window.scrollX}px`);
  }

  private clearShowTimeout(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
  }

  private clearHideTimeout(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  ngOnDestroy(): void {
    this.clearShowTimeout();
    this.clearHideTimeout();
    this.hide();
  }
}
